<template>
  <v-flex xs12 md12 lg12 class="pt-0 fill-height">
    <v-layout wrap>
      <v-alert v-if="!user.activated" type="error" :value="true">
        Your account is currently not activated. Please get in contact on our
        Slack channel and ask for activation.
      </v-alert>

      <v-alert v-if="!user.activated && !user.is_able_to_trade">
        Please ask on our Slack channel for activation. In the meanwhile you can
        start with entering your api keys (on the
        <router-link :to="{ name: 'settings' }">settings</router-link>page) and
        enabling some markets (on the
        <router-link :to="{ name: 'markets' }">markets</router-link>page). You
        can start the trade engine after activation.
      </v-alert>

      <v-flex xs12 lg12 md12>
        <v-card class="bordered-card fill-height">
          <v-card-title class="pb-0">
            <h2 class="title ml-3">Account</h2>
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-tile class="mb-4">
                <v-list-tile-content>
                  <v-list-tile-title>
                    Subscription:
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{ user.subscription_name }}
                    <v-icon v-if="user.lifetime_free" color="amber accent-3"
                      >fa-trophy</v-icon
                    >
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile
                v-if="
                  subscription.gsmg_fee_pct_per_trade === 0 &&
                    !user.lifetime_free
                "
                class="mb-4"
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    Expiry date ({{ timeZone }}):
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{
                      user.subscription_expiry_date_utc
                        | formatDate("DD MMM YYYY HH:mm")
                    }}
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile
                v-if="
                  subscription.gsmg_fee_pct_per_trade > 0 ||
                    user.subscription_name === 'Trial period'
                "
                class="mb-4"
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    Account fuel:
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{ user.credit_eth.toFixed(5) }} ETH (top-up below)
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile class="mb-4">
                <v-list-tile-content>
                  <v-list-tile-title>
                    Selected markets:
                    <v-icon
                      class="vertical-align-middle icon-for-information"
                      style="font-size: 16px;"
                      @click="
                        () => {
                          infoAlert(
                            $t('information.account_selected_markets.title'),
                            $t('information.account_selected_markets.description')
                          );
                        }
                      "
                  >fa-info-circle</v-icon
                  >
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    {{ enabledMarketCount > 0 ? enabledMarketCount : 0 }}
                    <span class="desc">
                      / {{ subscription.max_markets_across_exchanges }}
                    </span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-divider
                v-if="managedValueUSD.length > 0"
                class="divider"
              ></v-divider>
              <v-list-tile v-if="managedValueUSD.length > 0">
                <v-list-tile-content>
                  <v-list-tile-title>
                    Currently managed value (USD)
                    <v-icon
                      class="vertical-align-middle icon-for-information"
                      style="font-size: 16px;"
                      @click="
                        () => {
                          infoAlert(
                            $t('information.account_currently_managed_value.title'),
                            $t('information.account_currently_managed_value.description')
                          );
                        }
                      "
                      >fa-info-circle</v-icon
                    >
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
              <v-list-tile
                v-for="item in managedValueUSDCheckHuobi"
                :key="item.exchange"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <img
                      :src="
                        '/img/' + item.exchange + '_logo_' + themeMode + '.svg'
                      "
                      :alt="item.exchange"
                      width="20"
                      height="20"
                      class="mr-3"
                    />
                    {{ item.value }}
                    <span class="desc">
                      /
                      {{
                        subscription.max_account_value_per_exchange_usd
                      }}</span
                    >
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-flex>
</template>
<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range";
import swal from "sweetalert2";
import AccountValue from "./AccountValue";

export default {
  components: {
    AccountValue
  },
  data: function() {
    return {
      subscriptionHistoryDialog: false
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
      subscription: "auth/subscription",
      managedValueUSD: "accountValue/managed_value_usd",
      enabledMarketCount: "accountValue/enabled_market_count",
      themeDark: "settings/darkmode",
      accountValues: "accountValue/values",
    }),
    managedValueUSDCheckHuobi: function() {
      return this.managedValueUSD;
    },
    themeMode: function() {
      return this.themeDark ? "dark" : "light";
    },
    timeZone: function() {
      return moment()
        .tz(moment.tz.guess())
        .format("z");
    }
  },
  created: async function() {
    await this.$store.dispatch("accountValue/fetchAll");
  },
  methods: {
    infoAlert: async function(title, text) {
      await swal({
        title: title,
        html: text,
        showConfirmButton: false,
        showCloseButton: true,
        customClass: this.themeDark ? "swal-type-info-dark" : "swal-type-info"
      });
    },
  }
};
</script>

<style scoped>
.fill-height {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.theme--light .v-list__tile__title {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  color: #7395d0;
}
.theme--light .v-list__tile__sub-title {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 22px;
  color: #103a84;
}
.theme--light .v-list__tile__sub-title .desc {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  color: #7395d0;
}
.theme--light .divider {
  border-color: #e5efff;
}
.divider {
  color: rgba(235, 235, 245, 0.6);
}
.theme--dark .v-list__tile__title {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  color: rgba(235, 235, 245, 0.6);
}
.theme--dark .v-list__tile__sub-title {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 18px;
  line-height: 22px;
  color: #ffffff;
}
.theme--dark .v-list__tile__sub-title .desc {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  color: rgba(235, 235, 245, 0.6);
}
.theme--dark.v-list {
  background: #212121;
  color: #fff;
}
</style>



// WEBPACK FOOTER //
// resources/js/components/dashboard/AccountInfo.vue